import {useCallback, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode} from 'react'
import {createPortal} from 'react-dom'
import {Link as RouterLink} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {reportError} from '../../../common/errors/reportError'
import {addBreadcrumb} from '../../../common/errors/breadcrumb'
import {trackEvent} from '../../../common/analytics/trackEvent'
import {downloadFile} from '../../../common/utils/downloadFile'
import type {AnalyticsEventName} from '../../../common/analytics/events'
import {estimateRemainingSeconds, formatRemaining} from '../../../common/utils/estimateRemaining'
import {useTimedProgress} from './useTimedProgress'
import './ConverterShared.css'
import './ConverterDialog.css'

export interface ConverterAdFeat {
  key: string
  icon: ReactNode
}

export interface ConverterExportDialogProps {
  /** i18n namespace resolving `${namespace}.dialog.*` and `${namespace}.download.failed` */
  namespace: string
  /** Sentry breadcrumb/report category, e.g. 'image-converter' */
  breadcrumbCategory: string
  /** GA4 `upsell_shown` surface param + prefix for started/completed event names */
  analyticsSurface: string
  startedEvent: AnalyticsEventName
  completedEvent: AnalyticsEventName
  /** Target duration for the progress bar in 'synthetic' mode — omit for the
   * default random 7-10s window (image/icon/qr-style tools whose real work is
   * sub-second). Unused in 'real' mode. */
  durationMs?: number
  /** 'synthetic' (default): ease-out-cubic timer, capped at 95% until done — for
   * tools whose real work finishes too fast for a real progress signal to be
   * meaningful. 'real': the bar shows exactly what onGenerate reports via its
   * reportProgress callback (e.g. server-parsed ffmpeg progress) — no simulation,
   * no artificial floor duration. */
  progressMode?: 'synthetic' | 'real'
  /** 'real' mode only: shows a Cancel button and a time-left estimate (needs `${namespace}.dialog.cancel` / `.eta`). */
  cancellable?: boolean
  filename: string
  /** Hides the ad pane for the credited/no-upsell path */
  showAd: boolean
  adFeats: ConverterAdFeat[]
  adCtaTo: string
  /** `signal` aborts when the dialog is closed — generators that support it must stop working. */
  onGenerate: (reportProgress: (percent: number) => void, signal: AbortSignal) => Promise<Blob>
  onClose: () => void
}

const arrowIcon = (
  <svg
    width={13}
    height={13}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M5 12h14" />
    <path d="M13 5l7 7-7 7" />
  </svg>
)

const MIN_SYNTHETIC_DURATION_MS = 7000
const MAX_SYNTHETIC_DURATION_MS = 10000

const ConverterExportDialog = ({
  namespace,
  breadcrumbCategory,
  analyticsSurface,
  startedEvent,
  completedEvent,
  durationMs,
  progressMode = 'synthetic',
  cancellable = false,
  filename,
  showAd,
  adFeats,
  adCtaTo,
  onGenerate,
  onClose,
}: ConverterExportDialogProps) => {
  const {t} = useTranslation()
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [realProgress, setRealProgress] = useState(0)
  const [etaSeconds, setEtaSeconds] = useState<number | null>(null)
  const blobRef = useRef<Blob | null>(null)
  const startedAtRef = useRef(0)
  // Stable function identity via useCallback — the actual Math.random() call is
  // deferred until useTimedProgress's effect invokes it, never during render.
  const pickRandomDuration = useCallback(
    () => MIN_SYNTHETIC_DURATION_MS + Math.random() * (MAX_SYNTHETIC_DURATION_MS - MIN_SYNTHETIC_DURATION_MS),
    [],
  )
  // Always called (rules of hooks) — its output is simply unused in 'real' mode.
  const {progress: timedProgress, ready: timedReady} = useTimedProgress(done, durationMs ?? pickRandomDuration)
  const progress = progressMode === 'real' ? realProgress : timedProgress
  const ready = progressMode === 'real' ? done : timedReady

  const reportProgress = useCallback((percent: number) => {
    setRealProgress(percent)
    setEtaSeconds(estimateRemainingSeconds(performance.now() - startedAtRef.current, percent))
  }, [])

  // Kick off real generation in parallel with the progress timer. Parent mounts/unmounts
  // this component per open (fresh instance each time), so no reset effect is needed.
  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()
    startedAtRef.current = performance.now()
    addBreadcrumb(breadcrumbCategory, 'export-start', 'info', {gated: showAd})
    trackEvent(startedEvent, {gated: showAd})
    if (showAd) trackEvent('upsell_shown', {surface: analyticsSurface})
    onGenerate(reportProgress, controller.signal)
      .then((blob) => {
        if (cancelled) return
        blobRef.current = blob
        setDone(true)
        addBreadcrumb(breadcrumbCategory, 'export-ready', 'info', {size: blob.size})
        trackEvent(completedEvent, {size_bytes: blob.size, gated: showAd})
      })
      .catch((err: unknown) => {
        if (cancelled) return
        reportError(err, `${breadcrumbCategory}-export`, {gated: showAd})
        setError(t(`${namespace}.download.failed`))
      })
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [
    onGenerate,
    reportProgress,
    breadcrumbCategory,
    analyticsSurface,
    startedEvent,
    completedEvent,
    showAd,
    namespace,
    t,
  ])

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onClose])

  const handleDownload = useCallback(async () => {
    const blob = blobRef.current
    if (!blob) return
    await downloadFile(blob, filename)
    onClose()
  }, [filename, onClose])

  const handleOverlayClick = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose()
    },
    [onClose],
  )

  return createPortal(
    <div className="cvt-modal-overlay" role="dialog" aria-modal="true" onClick={handleOverlayClick}>
      <div className={showAd ? 'cvt-modal' : 'cvt-modal cvt-modal-no-ad'}>
        <header className="cvt-modal-header">
          <h3 className="cvt-modal-title">
            {ready ? t(`${namespace}.dialog.readyTitle`) : t(`${namespace}.dialog.title`)}
          </h3>
          <button
            type="button"
            className="cvt-modal-close"
            onClick={onClose}
            aria-label={t(`${namespace}.dialog.close`)}
          >
            ×
          </button>
        </header>

        <div className={showAd ? 'cvt-modal-body' : 'cvt-modal-body cvt-modal-body-solo'}>
          <div className="cvt-modal-progress-side">
            <p className="cvt-modal-sub">
              {ready ? t(`${namespace}.dialog.ready`) : t(`${namespace}.dialog.preparing`)}
            </p>

            {error && <p className="cvt-error">{error}</p>}

            {!ready && !error && (
              <div className="cvt-modal-progress-row">
                <div
                  className="cvt-modal-progress"
                  role="progressbar"
                  aria-label={t(`${namespace}.dialog.progressLabel`)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(progress)}
                >
                  <div className="cvt-modal-progress-bar" style={{transform: `translateX(${-(100 - progress)}%)`}} />
                </div>
                <span className="cvt-modal-progress-value">{Math.round(progress)}%</span>
              </div>
            )}

            {!ready && !error && progressMode === 'real' && cancellable && (
              <>
                {etaSeconds !== null && (
                  <p className="cvt-hint">{t(`${namespace}.dialog.eta`, {time: formatRemaining(etaSeconds)})}</p>
                )}
                <button type="button" className="cvt-btn" onClick={onClose}>
                  {t(`${namespace}.dialog.cancel`)}
                </button>
              </>
            )}

            {ready && !error && (
              <button type="button" className="cvt-btn cvt-btn-primary" onClick={handleDownload}>
                {t(`${namespace}.dialog.download`)}
              </button>
            )}
          </div>

          {showAd && (
            <aside className="cvt-modal-ad" aria-label={t(`${namespace}.dialog.ad.ariaLabel`)}>
              <div className="cvt-ad-header">
                <div className="cvt-ad-eyebrow">{t(`${namespace}.dialog.ad.eyebrow`)}</div>
                <div className="cvt-ad-heading">{t(`${namespace}.dialog.ad.heading`)}</div>
                <p className="cvt-ad-sub">{t(`${namespace}.dialog.ad.sub`)}</p>
              </div>
              <ul className="cvt-ad-feats">
                {adFeats.map(({key, icon}) => (
                  <li key={key} className="cvt-ad-feat">
                    <span className="cvt-ad-feat-icon" aria-hidden>
                      {icon}
                    </span>
                    <div className="cvt-ad-feat-body">
                      <div className="cvt-ad-feat-label">{t(`${namespace}.dialog.ad.feats.${key}.label`)}</div>
                      <div className="cvt-ad-feat-desc">{t(`${namespace}.dialog.ad.feats.${key}.desc`)}</div>
                    </div>
                  </li>
                ))}
              </ul>
              {/* Opens in a new tab: clicking this while a real (or fake) conversion is still
                  in progress must not navigate the current tab away and interrupt it. */}
              <RouterLink to={adCtaTo} className="cvt-ad-cta" target="_blank" rel="noopener noreferrer">
                <span className="cvt-ad-cta-main">
                  {t(`${namespace}.dialog.ad.cta`)}
                  {arrowIcon}
                </span>
                <span className="cvt-ad-cta-hint">{t(`${namespace}.dialog.ad.ctaHint`)}</span>
              </RouterLink>
            </aside>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default ConverterExportDialog
